import type {
  ParameterCoverage,
  TestAlertResult,
  AccessCheck,
  AgentCreated,
  AgentSummary,
  AnalysisResult,
  BackfillResult,
  ConnectionStatus,
  CredentialSummary,
  DailyCheck,
  DaybookResponse,
  EvidencePackage,
  FunnelDefinition,
  FunnelReportResponse,
  LivenessReport,
  NewUser,
  PaywallDimensionCheck,
  PaywallReport,
  PropertiesResponse,
  PropertyAdminView,
  PropertyRecord,
  QuotaReport,
  SessionInfo,
  TrackedEvent,
  UserPatch,
  UserSummary,
  VolatilitySuggestion,
  Watch,
} from './types'

/**
 * Thrown for any non-2xx response, carrying the backend's `detail` message so the UI
 * can show what actually went wrong rather than a generic failure.
 */
/**
 * Fired when the server says the session is gone, so the shell can return to the login
 * form instead of leaving every panel showing "Sign in to continue" as if it were a
 * data problem. An event rather than a callback: `request` has no view to talk to.
 */
export const UNAUTHORIZED_EVENT = 'ga4:unauthorized'

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export interface WindowParams {
  /** Last day of the window; omitted means the most recent settled day. */
  end?: string
  days?: number
  propertyId?: string
}

function buildQuery({ end, days, propertyId }: WindowParams): string {
  const params = new URLSearchParams()
  if (end) params.set('end', end)
  if (days !== undefined) params.set('days', String(days))
  if (propertyId) params.set('property_id', propertyId)
  const query = params.toString()
  return query ? `?${query}` : ''
}

async function request<T>(path: string, signal?: AbortSignal, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      signal,
      ...init,
      headers: { Accept: 'application/json', ...init?.headers },
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError('Could not reach the server.', 0)
  }

  if (!response.ok) {
    // Error bodies are FastAPI's `{detail: ...}`, but a proxy or crash can return
    // HTML, so fall back to the status text rather than surfacing a parse failure.
    let detail = response.statusText
    try {
      const body = await response.json()
      if (typeof body?.detail === 'string') detail = body.detail
      else if (Array.isArray(body?.detail)) detail = body.detail.map((item: { msg?: string }) => item.msg).join('; ')
    } catch {
      /* keep statusText */
    }
    // The login request itself is excluded: a wrong password is not an expired
    // session, and announcing one would clear the form the user is still using.
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    throw new ApiError(detail, response.status)
  }

  return (await response.json()) as T
}

export const api = {
  /** The current session, or a 401 — which is the cue to show the login form. */
  session: (signal?: AbortSignal) => request<SessionInfo>('/api/auth/me', signal),

  login: (username: string, password: string, signal?: AbortSignal) =>
    request<SessionInfo>('/api/auth/login', signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }),

  logout: (signal?: AbortSignal) =>
    request<{ signed_out: boolean }>('/api/auth/logout', signal, { method: 'POST' }),

  /** First run only: creates the first administrator and signs them in. */
  setup: (username: string, password: string, displayName: string, signal?: AbortSignal) =>
    request<UserSummary>('/api/admin/setup', signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, display_name: displayName }),
    }),

  adminUsers: (signal?: AbortSignal) => request<UserSummary[]>('/api/admin/users', signal),

  createUser: (user: NewUser, signal?: AbortSignal) =>
    request<UserSummary[]>('/api/admin/users', signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
    }),

  updateUser: (username: string, patch: UserPatch, signal?: AbortSignal) =>
    request<UserSummary[]>(`/api/admin/users/${encodeURIComponent(username)}`, signal, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }),

  deleteUser: (username: string, signal?: AbortSignal) =>
    request<UserSummary[]>(`/api/admin/users/${encodeURIComponent(username)}`, signal, {
      method: 'DELETE',
    }),

  adminAgents: (signal?: AbortSignal) => request<AgentSummary[]>('/api/admin/agents', signal),

  createAgent: (name: string, properties: string[], signal?: AbortSignal) =>
    request<AgentCreated>('/api/admin/agents', signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, properties }),
    }),

  updateAgent: (name: string, properties: string[], signal?: AbortSignal) =>
    request<AgentSummary[]>(`/api/admin/agents/${encodeURIComponent(name)}`, signal, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, properties }),
    }),

  deleteAgent: (name: string, signal?: AbortSignal) =>
    request<AgentSummary[]>(`/api/admin/agents/${encodeURIComponent(name)}`, signal, {
      method: 'DELETE',
    }),

  adminProperties: (signal?: AbortSignal) =>
    request<PropertyAdminView>('/api/admin/properties', signal),

  /** Service-account files on the host, described but never disclosed. */
  adminCredentials: (signal?: AbortSignal) =>
    request<CredentialSummary[]>('/api/admin/credentials', signal),

  /**
   * Store a service-account key. The browser reads the file and posts its text, so no
   * multipart dependency is needed on the server for one 2 KB upload.
   */
  uploadCredential: (filename: string, content: string, signal?: AbortSignal) =>
    request<CredentialSummary>('/api/admin/credentials', signal, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, content }),
    }),

  deleteCredential: (filename: string, signal?: AbortSignal) =>
    request<{ deleted: string }>(`/api/admin/credentials/${encodeURIComponent(filename)}`, signal, {
      method: 'DELETE',
    }),

  /** Live GA4 call: does this property's credential actually read this property? */
  checkPropertyAccess: (propertyId: string, signal?: AbortSignal) =>
    request<AccessCheck>(`/api/admin/properties/${encodeURIComponent(propertyId)}/check`, signal, {
      method: 'POST',
    }),

  /** Live Telegram send: does this property's configured alert chat actually receive? */
  sendTestAlert: (propertyId: string, signal?: AbortSignal) =>
    request<TestAlertResult>(`/api/admin/properties/${encodeURIComponent(propertyId)}/test-alert`, signal, {
      method: 'POST',
    }),

  saveAdminProperties: (properties: PropertyRecord[], signal?: AbortSignal) =>
    request<PropertyAdminView>('/api/admin/properties', signal, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(properties),
    }),

  properties: (signal?: AbortSignal) => request<PropertiesResponse>('/api/properties', signal),

  status: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<ConnectionStatus>(`/api/status${buildQuery(params)}`, signal),

  report: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<AnalysisResult>(`/api/report${buildQuery(params)}`, signal),

  eventReport: (eventName: string, params: WindowParams = {}, signal?: AbortSignal) =>
    request<AnalysisResult>(
      `/api/events/${encodeURIComponent(eventName)}${buildQuery(params)}`,
      signal,
    ),

  check: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<DailyCheck>(`/api/check${buildQuery(params)}`, signal),

  /** The evidence package — the exact payload an agent receives. */
  briefing: (
    params: WindowParams & { reportDate?: string; refresh?: boolean } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.reportDate) query.set('report_date', params.reportDate)
    if (params.refresh) query.set('refresh', 'true')
    if (params.propertyId) query.set('property_id', params.propertyId)
    const suffix = query.toString() ? `?${query}` : ''
    return request<EvidencePackage>(`/api/briefing${suffix}`, signal)
  },

  /**
   * The daybook stream: the leading day, then whatever history is stored.
   *
   * `days` here is a count of entries, not a reporting window, so it does not go
   * through `buildQuery` — sending it as the window length would ask the backend for a
   * seven-day report instead of seven daily verdicts.
   */
  days: (
    params: { end?: string; limit?: number; propertyId?: string } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.end) query.set('end', params.end)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.propertyId) query.set('property_id', params.propertyId)
    const suffix = query.toString() ? `?${query}` : ''
    return request<DaybookResponse>(`/api/days${suffix}`, signal)
  },

  /**
   * Are the tracked events still firing? Reads hours GA4 is still counting, so it is
   * the one call here whose numbers are provisional by design.
   */
  /** Parameter coverage per (event, parameter). Counts and percentages, never values. */
  coverage: (params: { propertyId?: string } = {}, signal?: AbortSignal) => {
    const query = params.propertyId ? `?property_id=${encodeURIComponent(params.propertyId)}` : ''
    return request<ParameterCoverage[]>(`/api/coverage${query}`, signal)
  },

  pulse: (params: { propertyId?: string; refresh?: boolean } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.propertyId) query.set('property_id', params.propertyId)
    if (params.refresh) query.set('refresh', 'true')
    const suffix = query.toString() ? `?${query}` : ''
    return request<LivenessReport>(`/api/pulse${suffix}`, signal)
  },

  /** Build and store entries for past days. The one read path that spends quota. */
  backfillDays: (
    params: { end?: string; days?: number; includeFunnels?: boolean; propertyId?: string } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.end) query.set('end', params.end)
    if (params.days !== undefined) query.set('days', String(params.days))
    if (params.includeFunnels) query.set('include_funnels', 'true')
    if (params.propertyId) query.set('property_id', params.propertyId)
    const suffix = query.toString() ? `?${query}` : ''
    return request<BackfillResult>(`/api/days/backfill${suffix}`, signal, { method: 'POST' })
  },

  /** One funnel on one day, with the recent end-to-end rates read from the daybook. */
  funnelReport: (
    name: string,
    params: { reportDate?: string; propertyId?: string } = {},
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams()
    if (params.reportDate) query.set('report_date', params.reportDate)
    if (params.propertyId) query.set('property_id', params.propertyId)
    const suffix = query.toString() ? `?${query}` : ''
    return request<FunnelReportResponse>(
      `/api/funnel/${encodeURIComponent(name)}${suffix}`,
      signal,
    )
  },

  /**
   * The paywall for one window: impressions, the actions taken on them, and the split
   * by `paywall_id`. 404 on a property with no paywall configured.
   */
  paywall: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<PaywallReport>(`/api/paywall${buildQuery(params)}`, signal),

  /**
   * Has paywall_id started carrying values? A live GA4 call over the processed hours,
   * made when somebody asks rather than on a poll — it spends quota to answer a
   * question about configuration, not about traffic.
   */
  paywallCheck: (params: { propertyId?: string } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams()
    if (params.propertyId) query.set('property_id', params.propertyId)
    const suffix = query.toString() ? `?${query}` : ''
    return request<PaywallDimensionCheck>(`/api/paywall/check${suffix}`, signal)
  },

  trackedEvents: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<TrackedEvent[]>(`/api/events${buildQuery(params)}`, signal),

  saveTrackedEvents: (events: TrackedEvent[], params: WindowParams = {}, signal?: AbortSignal) =>
    request<TrackedEvent[]>(`/api/events${buildQuery(params)}`, signal, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(events),
    }),

  funnels: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<FunnelDefinition[]>(`/api/funnels${buildQuery(params)}`, signal),

  saveFunnels: (funnels: FunnelDefinition[], params: WindowParams = {}, signal?: AbortSignal) =>
    request<FunnelDefinition[]>(`/api/funnels${buildQuery(params)}`, signal, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(funnels),
    }),

  watches: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<Watch[]>(`/api/watches${buildQuery(params)}`, signal),

  saveWatches: (watches: Watch[], params: WindowParams = {}, signal?: AbortSignal) =>
    request<Watch[]>(`/api/watches${buildQuery(params)}`, signal, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(watches),
    }),

  /** GA4 quota as of the last request. Costs nothing — GA4 returns it inline. */
  quota: (params: WindowParams = {}, signal?: AbortSignal) =>
    request<QuotaReport>(`/api/quota${buildQuery(params)}`, signal),

  suggestThresholds: (weeks = 8, params: WindowParams = {}, signal?: AbortSignal) => {
    const query = buildQuery(params)
    const separator = query ? '&' : '?'
    return request<VolatilitySuggestion>(
      `/api/watches/suggest${query}${separator}weeks=${weeks}`,
      signal,
    )
  },
}
